/**
 * The research source catalogue, fetched ONCE per page session.
 *
 * `available-sources` is a heavy discovery call (it walks the live index
 * catalogue), and it was being re-issued on every surface mount, every
 * StrictMode double-invoke and every remount behind a route change — the
 * backend log showed it being hammered.
 *
 * The catalogue is app-global and effectively static for the lifetime of a
 * page load, so it is cached in a module-level promise: concurrent callers
 * share ONE in-flight request, and later callers get the resolved value with
 * no network at all. A failure is not cached, so it can be retried.
 */

import { AppDispatch } from '../../store';
import {
  ResearchAvailableSources,
  researchGetAvailableSources,
} from '../../store/actions/ChatAction';

const EMPTY: ResearchAvailableSources = {
  jurisdiction_sources: [],
  user_datasources: [],
};

let cache: Promise<ResearchAvailableSources> | null = null;

/**
 * Resolve the catalogue, fetching at most once.
 *
 * `dispatch` is passed in rather than captured so this stays a plain function
 * the callers' hooks can await.
 */
export function loadSourceCatalogue(
  dispatch: AppDispatch
): Promise<ResearchAvailableSources> {
  if (cache) return cache;

  cache = Promise.resolve(dispatch(researchGetAvailableSources()))
    .then((result) => ({
      jurisdiction_sources: result?.jurisdiction_sources || [],
      user_datasources: result?.user_datasources || [],
    }))
    .catch((error) => {
      // Do NOT cache a failure — the next mount should be able to retry.
      cache = null;
      throw error;
    });

  return cache;
}

/** Drop the cache (sign-out, or an explicit refresh). */
export function invalidateSourceCatalogue(): void {
  cache = null;
}

/** Test seam: the value a failed load falls back to. */
export const EMPTY_CATALOGUE = EMPTY;
